import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  CustomerContainer,
  HeaderDiv,
  Heading,
  LeftDiv,
} from "pages/news/styles";
import { BackIconLogo } from "pages/news/components/news-details/styles";
import { NOTIFICATION } from "constants/route/route-constants";
import LoaderButton from "shared-components/loader-button";
import { loaderBtnStyleFunc } from "constants/style/props";
import { useAppDispatch } from "redux-store/hooks";
import { triggerNotificationThunk } from "slices/notifications";

import {
  DetailsContainer,
  BoxDiv,
  FlexDiv,
  FlexChild,
  FlexChildTwo,
  ImagePreview,
  DetailsActionsRow,
} from "./styles";

type ExtraInfoShape = {
  image_url?: string;
  action_type?: string;
  label?: string;
  link?: string;
  redirection?: string;
  redirectionScreen?: string;
  notificationCategory?: string;
  scheduledTime?: string;
  expiryTime?: string;
  scheduleDate?: string;
  expiryDate?: string;
  bodyHTML?: string;
  bodyArabicHTML?: string;
};

function formatDateBahrain(timedate: unknown): string {
  if (!timedate) {
    return "N/A";
  }
  const str = String(timedate).trim();
  if (!str) {
    return "N/A";
  }
  const day = new Date(str);
  if (Number.isNaN(day.getTime())) {
    return str;
  }
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Bahrain",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  };
  const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(day);
  let d = "", m = "", y = "";
  parts.forEach((p) => {
    if (p.type === "day") d = p.value;
    if (p.type === "month") m = p.value;
    if (p.type === "year") y = p.value;
  });
  const timeStr = day.toLocaleString("en-US", {
    timeZone: "Asia/Bahrain",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  return `${d}-${m}-${y} ${timeStr}`;
}

function displayVal(v: unknown): string {
  if (v === null || v === undefined) {
    return "N/A";
  }
  const s = String(v).trim();
  return s === "" ? "N/A" : s;
}

function NotificationDetails(): ReactElement {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const location = useLocation<{
    rowData?: Record<string, unknown>;
  }>();

  const row = location.state?.rowData;
  const dispatchStatus =
    row?.dispatchStatus != null ? String(row.dispatchStatus).trim() : "";
  const showTriggerButton = dispatchStatus.toLowerCase() === "saved";
  const notificationId = row?.id as number | undefined;

  useEffect(() => {
    if (!row) {
      history.replace(NOTIFICATION);
    }
  }, [row, history]);

  const rows = useMemo(() => {
    if (!row) {
      return [];
    }
    const ex = (row.extraInfo ?? {}) as ExtraInfoShape;
    const scheduledTimeVal =
      row.scheduledTime ??
      row.scheduleDate ??
      ex.scheduledTime ??
      ex.scheduleDate;
    const expiryTimeVal =
      row.expiryTime ??
      row.expiryDate ??
      ex.expiryTime ??
      ex.expiryDate;

    return [
      { label: "Notification ID", value: displayVal(row.id) },
      { label: "Brand", value: displayVal(row.brand) },
      { label: "Status", value: displayVal(row.status) },
      { label: "Dispatch status", value: displayVal(row.dispatchStatus) },
      {
        label: "Created on",
        value: formatDateBahrain(row.createdOn),
      },
      {
        label: "Dispatched on",
        value: formatDateBahrain(row.dispatchedOn),
      },
      {
        label: "Scheduled date & time",
        value: formatDateBahrain(scheduledTimeVal),
      },
      {
        label: "Expiry date & time",
        value: formatDateBahrain(expiryTimeVal),
      },
      { label: "User type", value: displayVal(row.role) },
      { label: "Header text (English)", value: displayVal(row.title) },
      { label: "Header text (Arabic)", value: displayVal(row.titleArabic) },
      { label: "Sub text (English)", value: displayVal(ex.bodyHTML ?? row.body) },
      { label: "Sub text (Arabic)", value: displayVal(ex.bodyArabicHTML ?? row.bodyArabic) },
      {
        label: "Category",
        value: displayVal(
          ex.notificationCategory ??
            (row.notificationType as string | undefined)
        ),
      },
      { label: "Action type", value: displayVal(ex.action_type) },
      {
        label: "App screen (redirection)",
        value: displayVal(ex.redirectionScreen),
      },
      { label: "Label", value: displayVal(ex.label) },
      { label: "Link URL", value: displayVal(ex.link) },
    ];
  }, [row]);

  const handelBack = () => {
    history.push({
      pathname: NOTIFICATION,
      state: { from: "Back" },
    });
  };

  const handleTriggerNotification = async () => {
    if (notificationId == null) {
      enqueueSnackbar("Notification ID is missing", { variant: "error" });
      return;
    }
    try {
      setLoading(true);
      const result = await dispatch(
        triggerNotificationThunk(notificationId)
      ).unwrap();
      enqueueSnackbar(result.data.status, { variant: "success" });
      setLoading(false);
      history.push({
        pathname: NOTIFICATION,
        state: { from: "Back" },
      });
    } catch (error: any) {
      setLoading(false);
      enqueueSnackbar(error?.data ?? "Failed to trigger notification", {
        variant: "error",
      });
    }
  };

  if (!row) {
    return <></>;
  }

  const ex = (row.extraInfo ?? {}) as ExtraInfoShape;
  const imageUrl = ex.image_url != null ? String(ex.image_url).trim() : "";

  return (
    <CustomerContainer>
      <HeaderDiv>
        <LeftDiv>
          <BackIconLogo onClick={handelBack} />
          <Heading>Notification details</Heading>
        </LeftDiv>
      </HeaderDiv>
      <DetailsContainer>
        <BoxDiv>
          {rows.map((item, ind) => {
            const isHtml =
              typeof item.value === "string" &&
              /<[a-z][\s\S]*>/i.test(item.value);
            return (
              <FlexDiv key={`notification-detail-${ind}`}>
                <FlexChild>{item.label}</FlexChild>
                <FlexChildTwo>
                  {isHtml ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: item.value as string }}
                    />
                  ) : (
                    item.value
                  )}
                </FlexChildTwo>
              </FlexDiv>
            );
          })}
          {imageUrl !== "" ? (
            <FlexDiv>
              <FlexChild>Image</FlexChild>
              <FlexChildTwo>
                <ImagePreview src={imageUrl} alt="Notification" />
              </FlexChildTwo>
            </FlexDiv>
          ) : null}
        </BoxDiv>
      </DetailsContainer>
      {/* {showTriggerButton ? (
        <DetailsActionsRow>
          <LoaderButton
            isLoading={loading}
            buttonText="Trigger Notification"
            styles={loaderBtnStyleFunc("auto", "2.6rem")}
            className="trigger-notification-btn"
            submitHandler={handleTriggerNotification}
          />
        </DetailsActionsRow>
      ) : null} */}
    </CustomerContainer>
  );
}

export default NotificationDetails;
